"use client";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  RefreshCwIcon,
} from "lucide-react";
import { ReactNode, useMemo, useState } from "react";

// ─── Types ─────────────────────────────────────────────────────────────────────

export type DataTableColumn<T> = {
  header: string;
  /** Tailwind width class, e.g. "w-[260px]" */
  width?: string;
  align?: "left" | "right";
  render: (row: T) => ReactNode;
  /** Unique key enabling sorting on this column; omit for non-sortable columns */
  sortKey?: string;
  /** Value to compare when sorting by sortKey; required when sortKey is set */
  sortValue?: (row: T) => string | number | null;
};

type SortState = { key: string; direction: "asc" | "desc" };

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[];
  data: T[];
  /** Must return a stable unique key for each row */
  getRowKey: (row: T) => string;
  loading?: boolean;
  /** When provided, a refresh icon button is shown */
  onRefresh?: () => void;
  /** When provided, rows are clickable */
  onRowClick?: (row: T) => void;
  /** Keys of rows to highlight as selected */
  selectedKeys?: Set<string>;
  /** When provided, search input is shown and rows are filtered using this function */
  searchFilter?: (row: T, query: string) => boolean;
  searchPlaceholder?: string;
  /** Optional content rendered to the left of the search input */
  searchActions?: ReactNode;
  emptyMessage?: string;
  /** Singular label used for the row count, e.g. "user" → "3 users" */
  itemLabel?: string;
  /** Plural of itemLabel when it is not just "s" appended, e.g. "entry" → "entries" */
  itemLabelPlural?: string;
  pageSize?: number;
  /** Total rows when `data` is only the current page of a server-paged list; replaces the row count shown */
  totalCount?: number;
  /** Let long text wrap onto more lines instead of being cropped at the column edge */
  wrapCells?: boolean;
};

const DEFAULT_PAGE_SIZE = 25;

// ─── Component ─────────────────────────────────────────────────────────────────

export function DataTable<T extends object>({
  columns,
  data,
  getRowKey,
  loading = false,
  onRefresh,
  onRowClick,
  selectedKeys,
  searchFilter,
  searchPlaceholder = "Search…",
  searchActions,
  emptyMessage = "No results found.",
  itemLabel = "item",
  itemLabelPlural,
  pageSize = DEFAULT_PAGE_SIZE,
  totalCount,
  wrapCells = false,
}: DataTableProps<T>) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState | null>(null);

  const filtered = useMemo(() => {
    if (!searchFilter) return data;
    const q = search.toLowerCase().trim();
    if (!q) return data;
    return data.filter((row) => searchFilter(row, q));
  }, [data, search, searchFilter]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find((c) => c.sortKey === sort.key);
    if (!column?.sortValue) return filtered;
    const { sortValue } = column;
    const dir = sort.direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filtered, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  // Removing the last row on a page must not strand the user on an empty page.
  if (page > totalPages) setPage(totalPages);
  const currentPage = Math.min(page, totalPages);
  const paged = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const toggleSort = (key: string) => {
    setPage(1);
    setSort((prev) => {
      if (prev?.key !== key) return { key, direction: "asc" };
      return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
    });
  };

  const plural = itemLabelPlural ?? `${itemLabel}s`;
  const countLabel =
    totalCount !== undefined
      ? `${totalCount} ${totalCount !== 1 ? plural : itemLabel}`
      : filtered.length === data.length
      ? `${data.length} ${data.length !== 1 ? (itemLabelPlural ?? `${itemLabel}s`) : itemLabel}`
      : `${filtered.length} of ${data.length} ${data.length !== 1 ? (itemLabelPlural ?? `${itemLabel}s`) : itemLabel}`;

  return (
    <div aria-busy={loading}>
      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">{loading && data.length === 0 ? <Skeleton className="h-5 w-20" /> : countLabel}</div>
        <div className="flex min-w-0 items-center gap-2">
          {searchFilter && (
            <>
              {searchActions}
              <Input
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full sm:w-64"
              />
            </>
          )}
          {onRefresh && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              onClick={onRefresh}
              disabled={loading}
              aria-label="Refresh"
            >
              <RefreshCwIcon className="size-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Table body */}
      {loading && <span role="status" className="sr-only">Loading…</span>}
      {!loading && paged.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </div>
      ) : (
        <>
          {/* Fade a plain wrapper, not <tbody>: an opacity animation on a table section
              leaves its rows unpainted after it ends until each row is hovered. */}
          <div className={cn(!loading && "content-fade-in")}>
            <Table className={wrapCells ? undefined : "table-fixed"}>
              <TableHeader>
                <TableRow>
                  {columns.map((col, i) => {
                    const isSorted = col.sortKey && sort?.key === col.sortKey;
                    const SortIcon = isSorted
                      ? sort?.direction === "asc"
                        ? ArrowUpIcon
                        : ArrowDownIcon
                      : ArrowUpDownIcon;
                    return (
                      <TableHead
                        key={i}
                        className={cn(
                          col.width,
                          col.align === "right" ? "text-right" : "",
                          wrapCells && "whitespace-normal",
                        )}
                      >
                        {col.sortKey ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(col.sortKey!)}
                            className={cn(
                              "group inline-flex items-center gap-1 text-left hover:text-foreground",
                              col.align === "right" ? "flex-row-reverse" : "",
                            )}
                          >
                            {col.header}
                            <SortIcon
                              className={cn(
                                "size-3.5 shrink-0",
                                isSorted ? "text-foreground" : "text-muted-foreground/50 group-hover:text-muted-foreground",
                              )}
                            />
                          </button>
                        ) : (
                          col.header
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && data.length === 0 ? Array.from({ length: Math.min(pageSize, 6) }, (_, rowIndex) => (
                  <TableRow key={rowIndex} aria-hidden="true">
                    {columns.map((col, i) => (
                      <TableCell key={i} className={cn("h-12", col.width)}>
                        <Skeleton className={cn("h-4 w-2/3 rounded", col.align === "right" && "ml-auto w-12")} />
                      </TableCell>
                    ))}
                  </TableRow>
                )) : paged.map((row) => (
                  <TableRow
                    key={getRowKey(row)}
                    className={cn(
                      onRowClick ? "cursor-pointer hover:bg-accent/50" : undefined,
                      selectedKeys?.has(getRowKey(row)) ? "bg-accent" : undefined,
                    )}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col, i) => (
                      <TableCell
                        key={i}
                        className={cn(
                          "text-sm",
                          wrapCells ? "whitespace-normal break-words align-top" : "overflow-hidden",
                          col.align === "right" ? "text-right tabular-nums" : "",
                          col.width,
                        )}
                      >
                        {col.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex shrink-0 items-center justify-between border-t border-border px-2 pt-3">
              <p className="text-xs text-muted-foreground">
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} of{" "}
                {filtered.length}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon className="size-4" />
                </Button>
                <span className="min-w-16 text-center text-xs text-muted-foreground">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <ChevronRightIcon className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
